import { CellContext } from '@tanstack/react-table';
import { getTableMeta, MAX_DISPLAYED_ALLOCATIONS_COUNT, PurchaseOrderRow } from '../../helpers';
import styles from './AllocationsCell.module.css';
import cn from 'classnames';
import { asMoneyWithoutZeros, roundTo2Dp } from 'helpers';
import { ProgressBar, Image } from '@round/ui-kit';
import { useCurrencies } from 'contexts/OptionsContext/useCurrencies';
import { Button } from '@round/whitelabel-ui';
import AllocationBadge from 'Modules/Plans/Finance/PurchaseOrders/components/AllocationBadge/AllocationBadge';
import { Tooltip } from '@round/whitelabel-ui';
import { platformLabels } from 'Modules/Plans/helpers';
import { Link } from 'react-router-dom';
import { CREATORBASE_ROOT_PATH } from 'Modules/Navigation/whitelabel/routes';
import range from 'lodash/range';
import CircleBadge from 'Modules/Plans/components/CircleBadge/CircleBadge';
import CampaignImage from 'Modules/Plans/Campaigns/components/CampaignImage/CampaignImage';
import { isEmptyObject } from 'utility/utility';

type Context = CellContext<PurchaseOrderRow, any>;

const AllocationsCell = ({ row: { original }, table }: Context) => {
    const {
        isLoading: isFetching,
        getIsCampaignLoading,
        getIsBrandLoading,
        getIsProjectLoading,
        getIsAllocationsLoading,
        onAllocatePurchaseOrder,
    } = getTableMeta(table);
    const isLoading = isFetching && isEmptyObject(original);
    const { getCurrencyById } = useCurrencies();
    const currency = getCurrencyById(original.currency_id);

    const amountAllocated = original.amount - original.remaining_amount;
    const percentAllocated = (amountAllocated / original.amount) * 100;

    return (
        <div className={styles.container}>
            <div className={styles.progressContainer}>
                <div className={styles.row}>
                    <span className={styles.amount}>
                        <b>{asMoneyWithoutZeros(amountAllocated, currency)}</b> /{' '}
                        {asMoneyWithoutZeros(original.amount, currency)}
                    </span>

                    <span className={styles.percentAllocated}>{roundTo2Dp(percentAllocated)}%</span>
                </div>
                <ProgressBar progress={percentAllocated} barClassName={styles.progressBarThumb} />
            </div>

            <div className={styles.allocationsContainer}>
                <div className={cn(styles.row, styles.alignStart)}>
                    <AllocationBadge originalAmount={original.amount} allocatedAmount={amountAllocated} />

                    <Button
                        className={styles.allocateButton}
                        appearance="primary"
                        type="button"
                        disabled={isLoading}
                        onClick={() => {
                            onAllocatePurchaseOrder(original);
                        }}
                    >
                        Manage
                    </Button>
                </div>

                <div className={styles.allocationsRow}>
                    {getIsAllocationsLoading(original) || isEmptyObject(original)
                        ? range(3).map((i) => <Allocation key={i} allocation={null} isLoading={true} />)
                        : original.allocations
                              .slice(0, MAX_DISPLAYED_ALLOCATIONS_COUNT)
                              .map((allocation) => (
                                  <Allocation
                                      key={allocation.id}
                                      allocation={allocation}
                                      isLoading={Boolean(
                                          getIsCampaignLoading(allocation) ||
                                              getIsBrandLoading(allocation) ||
                                              getIsProjectLoading(allocation)
                                      )}
                                  />
                              ))}
                    {original.allocations?.length > MAX_DISPLAYED_ALLOCATIONS_COUNT && (
                        <CircleBadge>+{original.allocations.length - MAX_DISPLAYED_ALLOCATIONS_COUNT}</CircleBadge>
                    )}
                </div>
            </div>
        </div>
    );
};

type Props =
    | {
          allocation: PurchaseOrderRow['allocations'][number];
          isLoading: boolean;
      }
    | {
          allocation: null;
          isLoading: true;
      };

function Allocation({ allocation, isLoading }: Props) {
    const { getCurrencyById } = useCurrencies();

    if (isLoading || !allocation) {
        return (
            <div className={styles.imageContainer}>
                <Image className={styles.image} src={''} loading={true} skeletonCircle={false} />
            </div>
        );
    }

    return (
        <Tooltip.Root>
            {(ref, setRef) => (
                <>
                    <Link
                        ref={setRef}
                        to={
                            allocation.project?.id
                                ? `/${CREATORBASE_ROOT_PATH}/projects/${allocation.project.id}?expanded=${
                                      allocation.campaign_id
                                  }&scrollToCampaign=${allocation.campaign_id}${
                                      allocation.campaign?.platform ? `#${allocation.campaign.platform}` : ''
                                  }`
                                : ''
                        }
                        target="_blank"
                        rel="noreferrer"
                        className={styles.imageContainer}
                    >
                        <CampaignImage
                            imageUrl={allocation?.project?.image || null}
                            alt={allocation.brand?.name ?? 'Brand image'}
                            managedBy={allocation.campaign?.managed_by ?? null}
                            platform={allocation.campaign?.platform ?? null}
                            className={styles.image}
                            isLoading={isLoading}
                        />
                    </Link>
                    <Tooltip anchorElement={ref}>
                        <Tooltip.Title>{allocation.campaign?.name ?? '-'}</Tooltip.Title>
                        <Tooltip.Body className={styles.tooltipContent}>
                            <p>
                                {allocation.project?.name} - {allocation.brand?.name}
                            </p>
                            {allocation.campaign?.platform && (
                                <p>Platform: {platformLabels[allocation.campaign.platform]}</p>
                            )}
                            <p className={styles.tooltipAllocatedAmount}>
                                Allocated:{' '}
                                {asMoneyWithoutZeros(
                                    allocation.amount,
                                    getCurrencyById(allocation.campaign?.currency_id)
                                )}
                            </p>
                        </Tooltip.Body>
                    </Tooltip>
                </>
            )}
        </Tooltip.Root>
    );
}

export default AllocationsCell;
